/**
 * /src/charts/bar-chart-race/BarChartRace.ts
 * BarChartRaceクラスの実装ファイル。
 * (ID化Protobuf + Mapping JSON 対応版 + 新仕様v2.0対応)
 */
import * as d3 from 'd3';
import { Selection, BaseType } from 'd3';
import { DataSet as BcrDataSetId } from '../../js/generated/bcr_data_id.pb.js';

type D3Selection<T extends BaseType> = Selection<T, unknown, null, undefined>;

interface BarChartRaceUIState {
  isPlaying: boolean;
  currentUiFrame: number;
  totalUiFrames: number;
}

interface AnimationProgressState {
  isPlaying: boolean;
  currentUiFrame: number;
  progressInFrame: number; // 0.0 ~ 1.0
}

type MappingData = {
  [id: number]: {
    id: number;
    name: string;
    fullName: string;
    platforms: string;
    releaseDate: string;
    category: string;
  }
};

type RehydratedFrame = {
  date: string;
  values: BarChartRaceValueItem[];
  isKeyframe: boolean;
  displayDate: string | null;
};

interface BarChartRaceValueItem {
  id: number;
  name: string;
  value: number;
  rank: number;
  fullName: string;
  platforms: string;
  releaseDate: string;
  category: string;
}

interface UIKeyframeMapItem {
  frameIndex: number;
  displayDate: string;
}

export class BarChartRace {
  // --- 1. プロパティ定義 ---
  private chartElement: HTMLElement;
  public config: any;
  private margin: { top: number; right: number; bottom: number; left: number };
  private width: number = 0;
  private height: number = 0;
  private lastKnownWidth: number = 0;

  private svg: D3Selection<SVGSVGElement> | null = null;
  private g: D3Selection<SVGGElement> | null = null;
  private xScale: d3.ScaleLinear<number, number> | null = null;
  private yScale: d3.ScaleBand<string> | null = null;
  private colorScale: d3.ScaleOrdinal<string, string> | null = null;
  private dateText: D3Selection<SVGTextElement> | null = null;
  private xAxisGroup: D3Selection<SVGGElement> | null = null;
  private tooltip: D3Selection<HTMLDivElement> | null = null;

  private decodedData: BcrDataSetId | null = null;
  private mappingData: MappingData | null = null;
  private keyframes: RehydratedFrame[] = [];
  private uiKeyframeMap: UIKeyframeMapItem[] = [];

  public isReady: boolean = false;
  private isPlaying: boolean = false;
  private animationFrameId: number | null = null;
  private totalDenseFrames: number = 0;
  public currentDenseFrame: number = 0;
  private currentUiFrame: number = 0;

  private lastLogicUpdateTime: number = 0;

  private onUpdateUICallback: (state: BarChartRaceUIState) => void = () => { };

  constructor(chartElement: HTMLElement, config = {}) {
    this.chartElement = chartElement;
    const defaultConfig = {
      protobufSourceUrl: null,
      mappingJsonSourceUrl: null,
      topN: 10,
      durationPerFrame: 500,
      transitionDuration: 500,
      autoplay: true,
      autoplayDelay: 500,
      barHeight: 50,
      labelFontSize: null,
      dateDisplayFormat: 'year_month',
      dateDisplayFontSize: null,
      initialXMax: 1000000,
      colorBy: 'name',
    };
    this.config = Object.assign({}, defaultConfig, config);
    this.margin = { top: 40, right: 10, bottom: 30, left: 50 };
  }

  public async init(): Promise<void> {
    const protoUrl = this.config.protobufSourceUrl;
    const mappingUrl = this.config.mappingJsonSourceUrl;

    if (!protoUrl || !mappingUrl) {
      this._displayMessage('データURLが指定されていません。', 'error');
      return;
    }

    try {
      const [protoResponse, mappingResponse] = await Promise.all([
        fetch(protoUrl),
        fetch(mappingUrl)
      ]);
      if (!protoResponse.ok) throw new Error(`Protobufの読み込みに失敗: ${protoResponse.statusText}`);
      if (!mappingResponse.ok) throw new Error(`マッピングJSONの読み込みに失敗: ${mappingResponse.statusText}`);
      const [protoBuffer, mappingJson] = await Promise.all([
        protoResponse.arrayBuffer(),
        mappingResponse.json()
      ]);
      this.mappingData = mappingJson;
      const decodedMessage = BcrDataSetId.decode(new Uint8Array(protoBuffer));
      this.decodedData = BcrDataSetId.toObject(decodedMessage, { defaults: true }) as BcrDataSetId;
      
      // 【修正】ローディングメッセージの非表示 (Role属性)
      const loadingMessage = this.chartElement.querySelector<HTMLElement>('[data-chart-role="loading"]');
      if (loadingMessage) loadingMessage.style.display = 'none';
      
      if (this.config.labelFontSize) this.chartElement.style.setProperty('--bar-label-font-size', `${this.config.labelFontSize}px`);
      if (this.config.dateDisplayFontSize) this.chartElement.style.setProperty('--date-display-font-size', `${this.config.dateDisplayFontSize}px`);
      this._processData();
      this.setupDimensions();
      this.lastKnownWidth = this.width;
      this.setupLayers();
      this._initialDrawAxes();
      this.isReady = true;
      this.drawFrame(0, true);
      this.updateUI();
      if (this.config.autoplay) {
        setTimeout(() => this.play(), this.config.autoplayDelay);
      }
    } catch (error) {
      this._displayMessage('データの読み込みまたは処理中にエラーが発生しました。', 'error');
      console.error("Failed to initialize BarChartRace:", error);
    }
  }

  public play(): void {
    if (this.isPlaying || !this.isReady) return;
    this.isPlaying = true;

    if (this.currentDenseFrame >= this.totalDenseFrames - 1) {
      this.currentDenseFrame = 0;
      this._resetBarsForPlayback();
    }

    this.lastLogicUpdateTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));

    this.updateUI();
  }

  public pause(): void {
    if (!this.isPlaying || this.animationFrameId === null) return;
    this.isPlaying = false;
    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
    this.updateUI();
  }

  public togglePlayPause(): void {
    this.isPlaying ? this.pause() : this.play();
  }

  public seek(uiFrameIndex: number): void {
    if (!this.isReady) return;
    
    // UIスライダーの補間値から最も近いキーフレームを探す
    // ※単純な整数インデックスだけでなく、小数値(0.01刻み)にも対応する場合のロジック
    // ここでは単純化のため、最も近い整数インデックスのマップエントリを使用
    const mapIndex = Math.min(Math.round(uiFrameIndex), this.uiKeyframeMap.length - 1);
    const mapItem = this.uiKeyframeMap[mapIndex];
    
    if (!mapItem) return;
    this.pause();

    const targetDenseFrame = mapItem.frameIndex;
    this.currentDenseFrame = targetDenseFrame;
    this.lastLogicUpdateTime = performance.now();

    this.drawFrame(this.currentDenseFrame, true);
    this.updateUI();
  }

  public getCurrentProgressState(): AnimationProgressState {
    let progressInFrame = 0;
    if (this.isPlaying) {
      const elapsedTime = performance.now() - this.lastLogicUpdateTime;
      progressInFrame = Math.min(1.0, elapsedTime / this.config.durationPerFrame);
    }
    return {
      isPlaying: this.isPlaying,
      currentUiFrame: this.currentUiFrame,
      progressInFrame: progressInFrame,
    };
  }

  public redrawOnResize(): void {
    const containerRect = this.chartElement.getBoundingClientRect();
    const newWidth = containerRect.width - this.margin.left - this.margin.right;
    if (Math.abs(newWidth - this.lastKnownWidth) < 10) { return; }
    this.lastKnownWidth = newWidth;
    const wasPlaying = this.isPlaying;
    this.pause();
    this.setupDimensions();
    this.setupLayers();
    this._initialDrawAxes();
    
    // 現在位置に近いUIフレームを再検索してシーク
    const currentUiFrame = this.uiKeyframeMap.findLastIndex(
      uiFrame => this.currentDenseFrame >= uiFrame.frameIndex
    );
    this.seek(Math.max(0, currentUiFrame));
    if (wasPlaying) {
      this.play();
    }
  }

  private animationLoop(timestamp: number): void {
    if (!this.isPlaying) return;

    const elapsed = timestamp - this.lastLogicUpdateTime;

    if (elapsed >= this.config.durationPerFrame) {
      this.currentDenseFrame++;

      if (this.currentDenseFrame >= this.totalDenseFrames) {
        this.currentDenseFrame = this.totalDenseFrames - 1;
        this.pause();
        this.updateUI();
        return;
      }

      this.drawFrame(this.currentDenseFrame);
      this.lastLogicUpdateTime = this.lastLogicUpdateTime + this.config.durationPerFrame;
      this.updateUI();
    }

    this.animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));
  }

  private drawFrame(denseFrameIndex: number, instant = false): void {
    if (!this.xScale || !this.yScale || !this.g || !this.colorScale || !this.xAxisGroup || !this.svg) return;
    const frameData = this.keyframes[denseFrameIndex];
    if (!frameData) return;
    const topNData = frameData.values.sort((a, b) => a.rank - b.rank).slice(0, this.config.topN);
    this.xScale.domain([0, d3.max(topNData, d => d.value) || this.config.initialXMax]);
    this.yScale.domain(topNData.map(d => d.id.toString()));
    const transitionDuration = instant ? 0 : this.config.transitionDuration;
    const xAxis = d3.axisTop(this.xScale).ticks(this.width / 120).tickSize(-this.height - 10).tickFormat(d => `${d.valueOf() / 10000}万`);
    this.xAxisGroup!.transition().duration(transitionDuration).ease(d3.easeLinear).call(xAxis as any);
    const yAxis = d3.axisLeft(this.yScale!).tickSize(0);
    const axesContainer = this.g.select<SVGGElement>(".axes-container");
    const yAxisSelection = axesContainer.selectAll("g.y-axis").data([null]).join("g").attr("class", "y-axis");
    yAxisSelection.transition().duration(transitionDuration).ease(d3.easeLinear).call(yAxis as any);
    const barsContainer = this.g.select<SVGGElement>(".bars-container");
    const bars = barsContainer.selectAll<SVGGElement, BarChartRaceValueItem>('g.bar-group').data(topNData, d => d.id);
    const enterBars = bars.enter().append('g').attr('class', 'bar-group').attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);
    enterBars.append('rect').attr('class', 'bar').attr('height', this.yScale.bandwidth()).attr('width', 0);
    const allBars = enterBars.merge(bars);
    allBars
      .on("mouseover", (event, d) => this._handleMouseOver(event, d))
      .on("mousemove", (event) => this._handleMouseMove(event))
      .on("mouseout", () => this._handleMouseOut());
    allBars.transition().duration(transitionDuration).ease(d3.easeLinear).attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);
    allBars.select<SVGRectElement>('rect.bar')
      .transition()
      .duration(transitionDuration)
      .ease(d3.easeLinear)
      .attr('width', d => Math.max(0, this.xScale!(d.value)))
      .attr('fill', d => this.colorScale!(this.config.colorBy === 'category' ? d.category : d.name));
    bars.exit().transition().duration(transitionDuration).ease(d3.easeLinear).attr('transform', `translate(0, ${this.height + 50})`).remove();
    const labelsContainer = this.g.select<SVGGElement>(".labels-container");
    const labels = labelsContainer.selectAll<SVGGElement, BarChartRaceValueItem>('g.label-group').data(topNData, d => d.id);
    const enterLabels = labels.enter().append('g').attr('class', 'label-group').attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);
    enterLabels.append('text').attr('class', 'bar-label-name');
    enterLabels.append('text').attr('class', 'bar-label-value');
    const allLabels = enterLabels.merge(labels);
    allLabels.transition().duration(transitionDuration).ease(d3.easeLinear).attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);
    const flipPoint = 80;
    allLabels.select<SVGTextElement>('text.bar-label-name').attr('y', this.yScale.bandwidth() / 2 - 2).text(d => d.name).transition().duration(transitionDuration).ease(d3.easeLinear).attr('x', d => this.xScale!(d.value) > flipPoint ? this.xScale!(d.value) - 5 : this.xScale!(d.value) + 5);
    allLabels.select<SVGTextElement>('text.bar-label-value').attr('y', this.yScale.bandwidth() / 2 + 15).transition().duration(transitionDuration).ease(d3.easeLinear).attr('x', d => this.xScale!(d.value) > flipPoint ? this.xScale!(d.value) - 5 : this.xScale!(d.value) + 5).textTween(function (d) {
      const self = d3.select(this);
      const startValue = +self.text().replace(/,/g, '') || d.value;
      const endValue = d.value;
      const interpolator = d3.interpolateNumber(startValue, endValue);
      return (time) => d3.format(',.0f')(interpolator(time));
    });
    allLabels.classed('is-inside', d => this.xScale!(d.value) > flipPoint).classed('is-outside', d => this.xScale!(d.value) <= flipPoint);
    labels.exit().transition().duration(transitionDuration).ease(d3.easeLinear).attr('transform', `translate(0, ${this.height + 50})`).remove();
    if (this.dateText) {
      const currentKeyframe = this.uiKeyframeMap.findLast(ui => denseFrameIndex >= ui.frameIndex);
      if (currentKeyframe) {
        let displayDate = currentKeyframe.displayDate;
        if (this.config.dateDisplayFormat === 'year_only') {
          const yearMatch = displayDate.match(/^\d{4}/);
          if (yearMatch) { displayDate = yearMatch[0]; }
        }
        this.dateText.text(displayDate);
      }
    }
  }

  public onUpdateUI(callback: (state: BarChartRaceUIState) => void): void {
    this.onUpdateUICallback = callback;
  }

  private updateUI(): void {
    if (typeof this.onUpdateUICallback !== 'function' || this.uiKeyframeMap.length === 0) { return; }

    const currentUiFrameIndex = this.uiKeyframeMap.findLastIndex(
      uiFrame => this.currentDenseFrame >= uiFrame.frameIndex
    );
    const currentUiFrame = Math.max(0, currentUiFrameIndex);

    let progress = 0;
    // 次のUIフレームが存在する場合のみ、進捗率を計算
    if (currentUiFrameIndex < this.uiKeyframeMap.length - 1) {
      const startDenseFrame = this.uiKeyframeMap[currentUiFrameIndex].frameIndex;
      const endDenseFrame = this.uiKeyframeMap[currentUiFrameIndex + 1].frameIndex;
      const totalFramesInStep = endDenseFrame - startDenseFrame;

      if (totalFramesInStep > 0) {
        const framesIntoStep = this.currentDenseFrame - startDenseFrame;
        progress = framesIntoStep / totalFramesInStep;
      }
    }

    // 小数点付きの滑らかな値を計算
    const smoothFrame = currentUiFrame + progress;

    this.onUpdateUICallback({
      isPlaying: this.isPlaying,
      currentUiFrame: smoothFrame,
      totalUiFrames: this.uiKeyframeMap.length,
    });
  }

  private _processData(): void {
    if (!this.decodedData || !this.mappingData) return;
    let lastMonth = '';
    this.keyframes = this.decodedData.frames.filter((frame): frame is { date: string; values: any[] } => typeof frame.date === 'string').map(frame => {
      const currentMonth = frame.date.substring(0, 7);
      const isKeyframe = (currentMonth !== lastMonth);
      lastMonth = currentMonth;
      return {
        date: frame.date,
        isKeyframe: isKeyframe,
        displayDate: isKeyframe ? currentMonth : null,
        values: frame.values.map(value => {
          const meta = this.mappingData![value.id];
          return { ...value, ...meta };
        })
      };
    });
    this.totalDenseFrames = this.keyframes.length;
    this.uiKeyframeMap = this.keyframes.map((frame, index) => ({ frame, index })).filter(item => item.frame.isKeyframe).map(item => ({
      frameIndex: item.index,
      displayDate: item.frame.displayDate!
    }));

    let colorDomainItems: string[];
    if (this.config.colorBy === 'category') {
      const categories = new Set(Object.values(this.mappingData).map(item => item.category));
      colorDomainItems = Array.from(categories);
    } else {
      const names = new Set(Object.values(this.mappingData).map(item => item.name));
      colorDomainItems = Array.from(names);
    }
    this.colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(colorDomainItems);
  }

  private setupDimensions(): void {
    const containerRect = this.chartElement.getBoundingClientRect();
    this.width = containerRect.width - this.margin.left - this.margin.right;
    this.height = this.config.topN * this.config.barHeight;
  }

  private setupLayers(): void {
    d3.select(this.chartElement).select("svg").remove();
    
    // 【修正】Role属性ベースのツールチップ探索
    // 親のコンテナラッパーを探し、その中のツールチップを取得
    const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
    if (wrapper) {
      let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip');
      if (tooltip.empty()) { 
        tooltip = d3.select(wrapper).append('div')
            .attr("class", 'chart-tooltip')
            .attr('data-chart-role', 'tooltip'); // Role属性追加
      }
      this.tooltip = tooltip;
    }
    
    const totalWidth = this.width + this.margin.left + this.margin.right;
    const totalHeight = this.height + this.margin.top + this.margin.bottom;
    this.svg = d3.select(this.chartElement).append("svg").attr("width", totalWidth).attr("height", totalHeight).attr("viewBox", [0, 0, totalWidth, totalHeight]);
    this.g = this.svg.append("g").attr("transform", `translate(${this.margin.left},${this.margin.top})`);
    this.g.append("g").attr("class", "bars-container");
    this.g.append("g").attr("class", "axes-container");
    this.g.append("g").attr("class", "labels-container");
  }

  private _initialDrawAxes(): void {
    if (!this.g || !this.svg || this.keyframes.length === 0) return;
    const axesContainer = this.g.select<SVGGElement>(".axes-container");
    this.xScale = d3.scaleLinear().range([0, this.width]);
    this.yScale = d3.scaleBand().range([0, this.height]).padding(0.1);
    this.xAxisGroup = axesContainer.append("g").attr("class", "x-axis").attr("transform", `translate(0, -5)`);
    axesContainer.append("g").attr("class", "y-axis");
    this.dateText = this.svg.append("text").attr("class", "chart-date-display").attr("x", this.width + this.margin.left - 10).attr("y", this.height + this.margin.top + this.margin.bottom - 10);
  }

  private _resetBarsForPlayback(): void {
    if (!this.g) return;
    this.g.select<SVGGElement>(".bars-container").selectAll<SVGGElement, BarChartRaceValueItem>('g.bar-group rect.bar').attr('width', 0);
    this.g.select<SVGGElement>(".labels-container").selectAll('g.label-group').remove();
  }

  // 【修正】Role属性ベースのエラーメッセージ表示
  private _displayMessage(message: string, type: 'error' | 'warning' = 'error'): void {
    d3.select(this.chartElement).select('[data-chart-role="message"]').remove();
    d3.select(this.chartElement).append('p')
        .attr('class', `chart-message ${type}`)
        .attr('data-chart-role', 'message')
        .text(message);
  }

  private _handleMouseOver(event: MouseEvent, d: BarChartRaceValueItem): void {
    if (!this.tooltip) return;
    const content = `
      <div class="tooltip-name">${d.fullName}</div>
      <div class="tooltip-detail">
        <span class="label">機種:</span>
        <span>${d.platforms}</span>
      </div>
      <div class="tooltip-detail">
        <span class="label">発売日:</span>
        <span>${d.releaseDate}</span>
      </div>
    `;
    this.tooltip.html(content).classed("is-visible", true);
  }

  private _handleMouseMove(event: MouseEvent): void {
    if (!this.tooltip) return;
    const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
    if (!wrapper) return;
    const [x, y] = d3.pointer(event, wrapper);
    const containerWidth = wrapper.offsetWidth;
    const tooltipWidth = this.tooltip.node()?.offsetWidth ?? 0;
    let left = x + 20;
    if (left + tooltipWidth > containerWidth) { left = x - tooltipWidth - 20; }
    let top = y + 20;
    this.tooltip.style("left", `${left}px`).style("top", `${top}px`);
  }

  private _handleMouseOut(): void {
    if (!this.tooltip) return;
    this.tooltip.classed("is-visible", false);
  }
}